import type { JSX } from 'react';
import { Badge } from '@/components/ui/badge';
import type { VerificationStatus } from '../types/verification.types';
import { cn } from '@/lib/utils';

interface VerificationStatusBadgeProps {
  status: VerificationStatus;
  className?: string | undefined;
}

const statusConfig: Record<
  VerificationStatus,
  {
    variant: 'default' | 'destructive' | 'secondary' | 'outline';
    label: string;
    customClass?: string;
  }
> = {
  DRAFT: {
    variant: 'secondary',
    label: 'Draft',
    customClass: 'bg-gray-100 text-gray-800 border-gray-300',
  },
  SENT: {
    variant: 'default',
    label: 'Sent',
    customClass: 'bg-blue-100 text-blue-800 border-blue-300',
  },
  ACCEPTED: {
    variant: 'secondary',
    label: 'Awaiting Inspection',
    customClass: 'bg-amber-100 text-amber-800 border-amber-300',
  },
  VERIFIED: {
    variant: 'default',
    label: 'Verified',
    customClass: 'bg-green-100 text-green-800 border-green-300',
  },
  RETURNED: {
    variant: 'outline',
    label: 'Returned',
    customClass: 'bg-purple-100 text-purple-800 border-purple-300',
  },
  REJECTED: {
    variant: 'destructive',
    label: 'Rejected',
    customClass: 'bg-red-100 text-red-800 border-red-300',
  },
  TIMED_OUT: {
    variant: 'outline',
    label: 'Timed Out',
    customClass: 'bg-orange-100 text-orange-800 border-orange-300',
  },
};

export function VerificationStatusBadge({
  status,
  className,
}: VerificationStatusBadgeProps): JSX.Element {
  const { variant, label, customClass } = statusConfig[status];
  return (
    <Badge variant={variant} className={cn(customClass, className)}>
      {label}
    </Badge>
  );
}
